import React, { useState } from 'react';
import { Button } from '../../ds/components/Button';
import { Icon } from '../../ds/components/Icon';
import { InputField } from '../../ds/InputField';
import type { DeviceOption } from './deviceOptionDefaults';
import {
  EMPTY_SELECTION,
  type RoomChoice,
  type Selection,
  type SelectionMode,
} from '../../utils/recommendationCatalog';

interface Props {
  onSave: (selection: Selection) => void;
  onBack: () => void;
  /** Nagłówek widoku = etykieta zalecenia. Jeden widok obsługuje każdą pozycję katalogu
   *  z polami wyboru (urządzenia i/lub pomieszczenia — migr. 059). LUSTRO widoku mobilki. */
  title?: string;
  /** Lista urządzeń TEJ pozycji (migr. 054; także wycofane — pokazujemy je tylko, gdy są
   *  zaznaczone w odtwarzanym zapisie, żeby dało się je odznaczyć). */
  deviceOptions?: DeviceOption[];
  devicesMode?: SelectionMode;
  allowOtherDevice?: boolean;
  /** Nagłówek listy urządzeń, gdy to nie „Typ urządzenia" (kratka, „Brak Went."). */
  deviceSectionTitle?: string;
  /** Pomieszczenia pozycji (podzbiór słownika, utils/recommendationCatalog.roomsForItem). */
  rooms?: RoomChoice[];
  roomsMode?: SelectionMode;
  allowOtherRoom?: boolean;
  /** Wybór odtworzony z zapisanej treści (parseSelections) — widok NIE startuje pusty. */
  initialSelection?: Selection;
}

/** LUSTRO jedynego słownika pomieszczeń mobilki (mobile/src/constants/heatSourceSchema.ts
 *  ROOM_OPTIONS — ten sam dla PG/COG i „Lokalizacji" źródła ciepła; audyt 2026-09-30 pkt 4).
 *  Parytet: backend/test/recommendationsMirrorParity.test.js. */
export const ROOM_OPTIONS: RoomChoice[] = [
  { id: 'kuchnia', label: 'Kuchnia' },
  { id: 'lazienka', label: 'Łazienka' },
  { id: 'wc', label: 'WC' },
  { id: 'kotlownia', label: 'Kotłownia' },
  { id: 'piwnica', label: 'Piwnica' },
  { id: 'garaz', label: 'Garaż' },
  { id: 'salon', label: 'Salon' },
  { id: 'sypialnia', label: 'Sypialnia' },
  { id: 'przedpokoj', label: 'Przedpokój' },
  { id: 'strych', label: 'Strych / poddasze' },
  { id: 'gospodarcze', label: 'Pom. gospodarcze' },
  { id: 'pokoj', label: 'Pokój' },
  { id: 'pralnia', label: 'Pralnia' },
  { id: 'pomieszczenie_kotla_co', label: 'Pomieszczenie kotła CO' },
];

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--text-body)',
  fontWeight: 'var(--weight-bold)',
  color: 'var(--text-heading)',
  margin: 'var(--space-3) 0 var(--space-1) 0',
};

const sectionSubtitleStyle: React.CSSProperties = {
  fontSize: 'var(--text-label)',
  color: 'var(--text-secondary)',
  margin: '0 0 var(--space-3) 0',
};

const toggle = (list: string[], id: string) =>
  (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

export const PgCogDetails: React.FC<Props> = ({
  onSave,
  onBack,
  title = 'Łącznik wymaga wymiany',
  deviceOptions = [],
  devicesMode = 'required',
  allowOtherDevice = false,
  deviceSectionTitle,
  rooms = ROOM_OPTIONS,
  roomsMode = 'required',
  allowOtherRoom = false,
  initialSelection = EMPTY_SELECTION,
}) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialSelection.devices);
  const [selectedRooms, setSelectedRooms] = useState<string[]>(initialSelection.rooms);
  const [otherDevice, setOtherDevice] = useState(initialSelection.otherDevice);
  const [otherDeviceOn, setOtherDeviceOn] = useState(initialSelection.otherDevice.trim().length > 0);
  const [otherRoom, setOtherRoom] = useState(initialSelection.otherRoom);
  const [otherRoomOn, setOtherRoomOn] = useState(initialSelection.otherRoom.trim().length > 0);

  // Wycofane z listy / spoza podzbioru pozycji, ale zaznaczone w starym zapisie — widoczne, żeby
  // dało się je odznaczyć (inaczej podmiana treści zgubiłaby je po cichu).
  const visibleDevices = deviceOptions.filter((o) => o.active !== false || selectedTypes.includes(o.id));
  const visibleRooms = [
    ...rooms,
    ...selectedRooms
      .filter((id) => !rooms.some((r) => r.id === id))
      .map((id) => ROOM_OPTIONS.find((r) => r.id === id) ?? { id, label: id }),
  ];
  const showDevices = devicesMode !== 'none';
  const showRooms = roomsMode !== 'none';
  // „Inne" liczy się tylko tam, gdzie pozycja na nie pozwala (inaczej pole jest ukryte i nie trafi do treści).
  const deviceCount = selectedTypes.length + (allowOtherDevice && otherDeviceOn && otherDevice.trim() ? 1 : 0);
  const roomCount = selectedRooms.length + (allowOtherRoom && otherRoomOn && otherRoom.trim() ? 1 : 0);
  // Lista wymagana, ale pusta (pozycja świeżo dodana w panelu, bez urządzeń) nie blokuje zapisu.
  const deviceRequired = devicesMode === 'required' && (visibleDevices.length > 0 || allowOtherDevice);

  // Zapis jest zablokowany przyciskiem (disabled) do czasu poprawnego wyboru — bez alert().
  const canSave = (!deviceRequired || deviceCount > 0) && (roomsMode !== 'required' || roomCount > 0);

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      devices: showDevices ? selectedTypes : [],
      otherDevice: showDevices && otherDeviceOn ? otherDevice.trim() : '',
      rooms: showRooms ? selectedRooms : [],
      otherRoom: showRooms && otherRoomOn ? otherRoom.trim() : '',
    });
  };

  const checkboxStyle = (checked: boolean, size: number): React.CSSProperties => ({
    width: size,
    height: size,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-sm)',
    border: `var(--border-width-strong) solid ${checked ? 'var(--status-ok-bar)' : 'var(--border-strong)'}`,
    background: checked ? 'var(--status-ok-bar)' : 'var(--surface-card)',
    color: 'var(--white)',
  });

  const optionButton = (key: string, label: string, isSelected: boolean, onClick: () => void) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        width: '100%',
        textAlign: 'left',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-3)',
        background: isSelected ? 'var(--status-ok-bg)' : 'var(--surface-card)',
        border: `var(--border-width-strong) solid ${isSelected ? 'var(--status-ok-bar)' : 'var(--border-strong)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        transition: 'border-color .12s ease, background-color .12s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={checkboxStyle(isSelected, 28)}>
        {isSelected && <Icon name="check" size={18} color="var(--white)" />}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 'var(--text-label)',
          fontWeight: isSelected ? 'var(--weight-bold)' : 'var(--weight-semibold)',
          color: isSelected ? 'var(--status-ok)' : 'var(--text-primary)',
        }}
      >
        {label}
      </span>
    </button>
  );

  const roomButton = (key: string, label: string, isSelected: boolean, onClick: () => void) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-pressed={isSelected}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'var(--space-2)',
        textAlign: 'center',
        padding: 'var(--space-3)',
        background: isSelected ? 'var(--status-ok-bg)' : 'var(--surface-card)',
        border: `var(--border-width-strong) solid ${isSelected ? 'var(--status-ok-bar)' : 'var(--border-strong)'}`,
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        transition: 'border-color .12s ease, background-color .12s ease',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <span style={checkboxStyle(isSelected, 24)}>
        {isSelected && <Icon name="check" size={14} color="var(--white)" />}
      </span>
      <span
        style={{
          minWidth: 0,
          fontSize: 'var(--text-label)',
          fontWeight: isSelected ? 'var(--weight-bold)' : 'var(--weight-semibold)',
          color: isSelected ? 'var(--status-ok)' : 'var(--text-primary)',
        }}
      >
        {label}
      </span>
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      <div style={{ marginBottom: 'var(--space-2)' }}>
        <Button variant="ghost" onClick={onBack} icon="chevron-right" style={{ paddingLeft: 0 }}>
          Wstecz
        </Button>
        <h2
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-h2)',
            fontWeight: 'var(--weight-bold)',
            color: 'var(--text-heading)',
          }}
        >
          {title}
        </h2>
      </div>

      {showDevices && (
        <>
          <h3 style={sectionTitleStyle}>
            {deviceSectionTitle
              ?? (devicesMode === 'required' ? 'Typ urządzenia:' : 'Typ urządzenia (opcjonalnie):')}
          </h3>
          <p style={sectionSubtitleStyle}>Wybierz wszystkie które mają zastosowanie</p>

          {visibleDevices.map((type) =>
            optionButton(
              type.id,
              `${type.label}${type.active === false ? ' (wycofane)' : ''}`,
              selectedTypes.includes(type.id),
              () => setSelectedTypes(toggle(selectedTypes, type.id)),
            ))}
          {allowOtherDevice && optionButton('__inne__', 'Inne (wpisz)', otherDeviceOn, () => setOtherDeviceOn(!otherDeviceOn))}
          {allowOtherDevice && otherDeviceOn && (
            <InputField
              label="Inne — wpisz"
              value={otherDevice}
              maxLength={200}
              onChange={(e) => setOtherDevice(e.target.value)}
            />
          )}
        </>
      )}

      {showRooms && (
        <>
          <h3 style={sectionTitleStyle}>
            {roomsMode === 'required' ? 'Pomieszczenie:' : 'Pomieszczenie (opcjonalnie):'}
          </h3>
          <p style={sectionSubtitleStyle}>Wybierz wszystkie które mają zastosowanie</p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
            }}
          >
            {visibleRooms.map((room) =>
              roomButton(room.id, room.label, selectedRooms.includes(room.id),
                () => setSelectedRooms(toggle(selectedRooms, room.id))))}
            {allowOtherRoom && roomButton('__inne__', 'Inne (wpisz)', otherRoomOn, () => setOtherRoomOn(!otherRoomOn))}
          </div>
          {allowOtherRoom && otherRoomOn && (
            <InputField
              label="Inne pomieszczenie — wpisz"
              value={otherRoom}
              maxLength={200}
              onChange={(e) => setOtherRoom(e.target.value)}
            />
          )}
        </>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 'var(--space-3)',
          paddingTop: 'var(--space-4)',
          borderTop: 'var(--border-width) solid var(--border)',
        }}
      >
        <Button variant="secondary" onClick={onBack}>
          Anuluj
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!canSave}
          icon="check"
        >
          Zapisz ({canSave ? '✓' : '...'})
        </Button>
      </div>
    </div>
  );
};
